import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { inr, num } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { FileBarChart, Download, IndianRupee } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { toast } from "sonner";

const MONTH_LABEL = (m) => {
  const [y, mm] = m.split("-");
  return new Date(Number(y), Number(mm) - 1, 1).toLocaleDateString("en-IN", { month: "short", year: "numeric" });
};

export default function Statements() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/reports/gst").then((r) => setData(r.data)).catch(() => setData(false));
  }, []);

  if (!data) return <p className="text-muted-foreground">Loading…</p>;

  const role = user.role;
  const months = data.months;

  const cols =
    role === "merchant"
      ? [
          { key: "gross", label: "Collections" },
          { key: "platform_fee", label: "Platform Fee" },
          { key: "merchant_net", label: "Net Settlement" },
        ]
      : role === "distributor"
      ? [
          { key: "gross", label: "Downline Volume" },
          { key: "distributor_earning", label: "Commission" },
        ]
      : [
          { key: "gross", label: "Volume" },
          { key: "platform_fee", label: "Platform Fee" },
          { key: "distributor_earning", label: "Distributor Payout" },
          { key: "superadmin_earning", label: "Net Revenue" },
        ];

  const chartData = [...months].reverse().map((m) => ({ month: MONTH_LABEL(m.month).split(" ")[0], gross: m.gross }));

  const exportCsv = () => {
    const header = ["Month", "Transactions", ...cols.map((c) => c.label)];
    const lines = months.map((m) => [MONTH_LABEL(m.month), m.count, ...cols.map((c) => m[c.key].toFixed(2))].join(","));
    const csv = [header.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `woltpe_gst_statement_${Date.now()}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success("Statement exported");
  };

  const totals = cols.reduce((acc, c) => ({ ...acc, [c.key]: months.reduce((s, m) => s + m[c.key], 0) }), {});
  const totalCount = months.reduce((s, m) => s + m.count, 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">Monthly Statements</h2>
          <p className="text-sm text-muted-foreground">Month-wise collections, fees and net — ready for GST filing.</p>
        </div>
        <Button variant="outline" onClick={exportCsv} disabled={months.length === 0} data-testid="export-statement-button">
          <Download className="mr-1.5 h-4 w-4" /> Export statement
        </Button>
      </div>

      {months.length === 0 ? (
        <Card className="p-10 text-center">
          <FileBarChart className="h-8 w-8 mx-auto text-muted-foreground" />
          <p className="mt-3 font-medium">No settled transactions yet</p>
          <p className="text-sm text-muted-foreground">Monthly summaries appear once collections succeed.</p>
        </Card>
      ) : (
        <>
          <Card className="p-5">
            <h3 className="font-heading font-semibold mb-4 flex items-center gap-2"><IndianRupee className="h-4 w-4" /> Volume by month</h3>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ left: -12, right: 6, top: 6 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false}
                    tickFormatter={(v) => (v >= 1000 ? `${v / 1000}k` : v)} />
                  <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 13 }}
                    formatter={(v) => [inr(v), "Volume"]} cursor={{ fill: "hsl(var(--muted))" }} />
                  <Bar dataKey="gross" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} maxBarSize={48} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card className="p-4 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Month</TableHead>
                  <TableHead className="text-right">Txns</TableHead>
                  {cols.map((c) => <TableHead key={c.key} className="text-right">{c.label}</TableHead>)}
                </TableRow>
              </TableHeader>
              <TableBody>
                {months.map((m) => (
                  <TableRow key={m.month} data-testid={`statement-row-${m.month}`}>
                    <TableCell className="font-medium">{MONTH_LABEL(m.month)}</TableCell>
                    <TableCell className="text-right font-mono">{num(m.count)}</TableCell>
                    {cols.map((c) => (
                      <TableCell key={c.key} className={`text-right font-mono ${c.key === "merchant_net" || c.key === "superadmin_earning" || c.key === "distributor_earning" ? "text-success" : ""}`}>
                        {inr(m[c.key])}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                <TableRow className="border-t-2 font-semibold">
                  <TableCell>Total</TableCell>
                  <TableCell className="text-right font-mono">{num(totalCount)}</TableCell>
                  {cols.map((c) => <TableCell key={c.key} className="text-right font-mono">{inr(totals[c.key])}</TableCell>)}
                </TableRow>
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </div>
  );
}
